import {
  EntityInfo,
  TestTimingSettingType
} from "../../../../types/gqlTypes/graphql"
import { EocClientStudyPlan, PlanIntent, StudyPlanScope } from "../../types"
import { Context, SeviceErrors, StudyPlanTargets, Transitions } from "./types"
import { DEFAULT_ERROR_MESSAGE, NO_SUBJECT_SELECTED_ERROR } from "../constants"
import { getDefaultErrorMessage } from "../helpers"
import { TimingOptionsValue } from "../../study-plans/components/TimingOptions/types"
import { STUDY_PLAN_V2_SUBPAGE_TARGET_MAP } from "../../study-plans/pages/StudyPlansV2/constants"

export const getPlanIntent = (
  publishedPlan: EocClientStudyPlan,
  selectedEntity: EntityInfo,
  selfRefPlanScope: StudyPlanScope
): PlanIntent => {
  if (!publishedPlan) return PlanIntent.NEW

  if (selectedEntity) {
    return publishedPlan.meta.isRecommended
      ? PlanIntent.FROM_RECOMMENDED
      : PlanIntent.FROM_MY
  }

  return publishedPlan.refScope.scope === selfRefPlanScope
    ? PlanIntent.FROM_MY
    : PlanIntent.FROM_RECOMMENDED
}

export const getPlanScope = (
  entityId: string,
  isStudent: boolean,
  selfRefPlanScope: StudyPlanScope
): StudyPlanScope => {
  if (!entityId) {
    return selfRefPlanScope
  }

  if (isStudent) {
    return "STUDENT"
  }

  return "CLASS"
}

export const getServiceErrorMessage = (
  service: SeviceErrors,
  data?: { errorReason?: string; messageData?: string[] }
) => {
  const { errorReason, messageData } = data ?? {}

  switch (service) {
    case "getSectionAvailableItems": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Section not found"
      )
        return `Unable to ${"retrieve the list of Mastery Lessons"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "Boot Camps are not currently supported")
        return getDefaultErrorMessage("retrieve the list of Mastery Lessons")
      return DEFAULT_ERROR_MESSAGE
    }

    case "editPlanDuration": {
      if (errorReason === "Plan not found")
        return `Unable to ${"update your plan`s hours"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "Error updating plan")
        return getDefaultErrorMessage("update your plan`s hours")
      return DEFAULT_ERROR_MESSAGE
    }

    case "undo": {
      if (errorReason === "Plan not found")
        return `Unable to ${"undo"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "cannot undo") return getDefaultErrorMessage("undo")
      return DEFAULT_ERROR_MESSAGE
    }

    case "redo": {
      if (errorReason === "Plan not found")
        return `Unable to ${"redo"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "cannot redo") return getDefaultErrorMessage("redo")
      return DEFAULT_ERROR_MESSAGE
    }

    case "addSection": {
      if (errorReason === "Plan not found")
        return `Unable to ${"add section"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "Error updating plan")
        return getDefaultErrorMessage("add section")
      return DEFAULT_ERROR_MESSAGE
    }

    case "removeSection": {
      if (errorReason === "Plan not found")
        return `Unable to ${"remove section"}. Someone else may be editing this plan. Please try again later.`
      if (
        errorReason === "Section Remove failed" ||
        errorReason === "Error updating plan"
      )
        return getDefaultErrorMessage("remove section")
      return DEFAULT_ERROR_MESSAGE
    }

    case "updateSection": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Section not found"
      )
        return `Unable to ${"add a break"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "Error updating plan")
        return getDefaultErrorMessage("add a break")
      return DEFAULT_ERROR_MESSAGE
    }

    case "updateSectionSecond": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Section not found"
      )
        return `Unable to ${"update the hours for section"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "Error updating plan")
        return `An error occurred while trying to ${"update the hours for section"}. Please try again. If the error persists, contact support for help.`
      return DEFAULT_ERROR_MESSAGE
    }

    case "addSectionItem": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Section not found" ||
        errorReason === "ItemList not found" ||
        errorReason === "Content Item not found"
      )
        return `Unable to ${"add item"}. Someone else may be editing this plan. Please try again later.`
      if (
        errorReason === "Item is already assigned to this Section" ||
        errorReason === "Item is already assigned to another Section"
      )
        return `${"LESSON_TYPE"} is already assigned to ${"section"}.`
      if (
        errorReason === "Boot Camps are not currently supported" ||
        errorReason === "Error updating plan"
      )
        return getDefaultErrorMessage("add item")
      return DEFAULT_ERROR_MESSAGE
    }

    case "removeSectionItem": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Section not found" ||
        errorReason === "ItemList not found"
      )
        return `Unable to ${"remove item"}. Someone else may be editing this plan. Please try again later.`
      if (
        errorReason === "Remove Section Item failed" ||
        errorReason === "Error updating plan"
      )
        return getDefaultErrorMessage("remove item")
      return DEFAULT_ERROR_MESSAGE
    }

    case "updateSectionItem": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Section not found" ||
        errorReason === "ItemList not found"
      )
        return `Unable to ${"update section"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "Error updating plan")
        return getDefaultErrorMessage("update section")
      return DEFAULT_ERROR_MESSAGE
    }

    case "resolveStudentConflict": {
      return `An error occurred. Please try again. If the error persists, contact support for help.`
    }

    case "activatePlan": {
      if (errorReason === "Plan not found")
        return `Unable to ${"save"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "user does not have permission to manage plan")
        return `Unable to save. This account does not have permission to make changes to study plans.`
      return DEFAULT_ERROR_MESSAGE
    }

    case "reorderSectionItem": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Item's Original Section not found" ||
        errorReason === "Target item not found"
      )
        return `Unable to ${"reorder item"}. Someone else may be editing this plan. Please try again later.`
      if (
        errorReason === `Destination Section not found` ||
        errorReason === "Error updating plan"
      )
        return getDefaultErrorMessage(messageData?.[0])
      return DEFAULT_ERROR_MESSAGE
    }

    case "reorderSection": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Target section not found"
      )
        return `Unable to ${"reorder section"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "Error updating plan")
        return getDefaultErrorMessage(messageData?.[0])
      return DEFAULT_ERROR_MESSAGE
    }

    case "newPlan": {
      if (errorReason === "Plan cannot be over 30 hours")
        return `Plan cannot be over 30 hours. Please modify hours and try again.`
      if (errorReason === "Plan cannot be less than 5 hours")
        return `Plan cannot be less than 5 hours. Please modify hours and try again.`
      if (errorReason === "Error creating draft plan")
        return getDefaultErrorMessage(messageData?.[0])
      return DEFAULT_ERROR_MESSAGE
    }

    case "cancelPlan": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Error trying to cancel plan edits"
      )
        return getDefaultErrorMessage(messageData?.[0])
      return DEFAULT_ERROR_MESSAGE
    }

    case "moveItemToNewSection": {
      if (
        errorReason === "Plan not found" ||
        errorReason === "Section not found" ||
        errorReason === "Target item not found"
      )
        return `Unable to ${"move item to section"}. Someone else may be editing this plan. Please try again later.`
      if (errorReason === "Error updating plan")
        return getDefaultErrorMessage(messageData?.[0])
      return DEFAULT_ERROR_MESSAGE
    }

    case "getCurrentPlan": {
      if (errorReason === "No Current Plan found")
        return getDefaultErrorMessage(messageData?.[0])
      return DEFAULT_ERROR_MESSAGE
    }

    case "getCurrentPlanForEachSubjectByRefScope":
    case "getTeacherRelevantPlanInfo":
    case "getAdminRelevantPlanInfo": {
      if (errorReason === "Plans not found")
        return getDefaultErrorMessage(messageData?.[0])

      return DEFAULT_ERROR_MESSAGE
    }

    case "getHistoryEdit":
    case "confirmOverride":
    case "downloadResources":
    case "reachedMaxHours":
    case "reachedMinHours": {
      return `An error occurred. Someone else may be editing this plan. Please try again later. If the error persists, contact support for help.`
    }
    case "changeMicroSnapsCount": {
      return "An error occurred on changing lesson count. Please exit the page then try again. If the error persists, contact support for help."
    }

    case "getMaxAvailableContentDuration":
    case "getSkillReportByContentId": {
      return DEFAULT_ERROR_MESSAGE
    }

    case "toggleBreakBeforeSection":
    case "toggleBreakBeforeLesson":
    case "toggleBreakAfterSection":
    case "toggleBreakAfterLesson":
    case "restoreRecommendedPlan":
    case "changeToManualPlan":
    case "changeToAdaptivePlan":
    // NOTE: will use after modifications of getPlanHistory
    // case "getEditHistory":
    case "assignSubjectToClasses":
    case "fetchClasses":
    case "fetchDraftStudyPlan":
    case "getGlobalSettings":
    case "getPlanAvailableItems":
    case "getRecommendedPlan":
    case "toggleItemTeachLive":
    case "getAssignedSubjectsByRefScope":
    case "getLessonPreview":
    case "getLessonDetails":
    case "replaceSectionItem":
    case "togglePublishedMarkComplete":
    case "toggleDraftMarkComplete":
    case "toggleStudyPlansOutOfOrder":
    case "toggleResetMasteryLessons":
    case "saveStudyPlanSettings":
    case "getProgressDataByCurrentPlan":
    case "getProgressBySelectedStudyPlanSubject":
    case "getProgressByStudyPlanSubjects":
    case "getSkillReportContentTargets": {
      return getDefaultErrorMessage(messageData?.[0])
    }

    case "getScoresByStudent": {
      // Return hard-coded copy only; never surface the raw API message.
      if (
        errorReason === NO_SUBJECT_SELECTED_ERROR ||
        errorReason?.includes("data.subject")
      ) {
        return "Unable to load scores by student without a selected subject. Please select a subject."
      }
      return getDefaultErrorMessage(messageData?.[0])
    }
    case "downloadScoresByStudent": {
      return getDefaultErrorMessage("download the scores by student")
    }

    default:
      return DEFAULT_ERROR_MESSAGE
  }
}

export const getTimingOptionByValue = (
  value: number,
  mode: TestTimingSettingType = "PERCENTAGE"
) => {
  if (value === 1 && mode === "PERCENTAGE") {
    return TimingOptionsValue.standard
  }

  if (value < 0) {
    return TimingOptionsValue.unlimited
  }

  return TimingOptionsValue.custom
}

export const getStudyPlanStorageKey = (key: string, userId: string) =>
  `${key}_${userId}`

export const getCurrentTargetOnSetStateAction = (
  ctx: Context,
  evt: Extract<Transitions, { type: "SET_STATE" }>
) => {
  let currentTarget: StudyPlanTargets | undefined

  if (ctx.isStudyPlanV2Enabled) {
    if (["view", "edit"].includes(evt.subpage)) {
      currentTarget = StudyPlanTargets.ManageStudyPlans
    } else if (STUDY_PLAN_V2_SUBPAGE_TARGET_MAP[evt.subpage]) {
      currentTarget = STUDY_PLAN_V2_SUBPAGE_TARGET_MAP[evt.subpage]
    }

    return currentTarget
  }

  if (evt.subpage === "view" && evt.entityType === "recommended") {
    currentTarget = StudyPlanTargets.ViewRecommended
  } else if (evt.subpage === "view") {
    currentTarget = StudyPlanTargets.ReviewPlan
  } else if (evt.subpage === "edit") {
    currentTarget = StudyPlanTargets.Edit
  } else if (ctx.user?.role === "Teacher" && !evt.subpage && !!evt.subject) {
    currentTarget = StudyPlanTargets.TeacherView
  } else if (
    (ctx.user?.role === "District Admin" ||
      ctx.user?.role === "School Admin") &&
    !!evt.subject
  ) {
    currentTarget = StudyPlanTargets.Overview
  } else {
    currentTarget = StudyPlanTargets.Landing
  }

  return currentTarget
}
